import { IconBackground } from "@tabler/icons-react";
import { type ReactNode } from "react";

import { Button } from "#app/components/ui/button";
import { cn } from "#app/utils/misc.tsx";

type EmptyStateProps = {
  title: string;
  description?: string;
  icon?: ReactNode;
  buttonText?: string;
  buttonIcon?: ReactNode;
  onButtonClick?: () => void;
  buttonVariant?:
    | "default"
    | "secondary"
    | "outline"
    | "destructive"
    | "ghost"
    | "link"
    | "gradient";
  buttonDisabled?: boolean;
  className?: string;
};

export function EmptyState({
  title,
  description,
  icon = (
    <IconBackground
      size={30}
      stroke={1.8}
      className="text-red-500 dark:text-red-300"
    />
  ),
  buttonText,
  buttonIcon,
  onButtonClick,
  buttonVariant = "default",
  buttonDisabled = false,
  className,
}: EmptyStateProps) {
  return (
    <div
      className={cn(
        "relative flex min-h-100 flex-col items-center justify-center px-6 text-center",
        className,
      )}
    >
      {/* Graph-paper backdrop, faded out towards the edges. */}
      <div
        aria-hidden
        className="absolute inset-0 z-0 bg-[#E5E5F7] opacity-10 [background-image:linear-gradient(#5f6184_1px,transparent_1px),linear-gradient(90deg,#5f6184_1px,transparent_1px),linear-gradient(#5f6184_0.5px,transparent_0.5px),linear-gradient(90deg,#5f6184_0.5px,#E5E5F7_0.5px)] [background-position:-1px_-1px,-1px_-1px,-0.5px_-0.5px,-0.5px_-0.5px] [background-size:50px_50px,50px_50px,10px_10px,10px_10px] [mask-image:radial-gradient(ellipse_at_center,rgba(0,0,0,0.7)_0%,rgba(0,0,0,0)_75%)] [mask-repeat:no-repeat] [mask-size:100%_100%] dark:bg-zinc-900 dark:opacity-20"
      />

      <div className="relative z-10">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl border bg-white dark:border-zinc-700 dark:bg-zinc-900">
          {icon}
        </div>

        <h3 className="mt-6 text-xl font-semibold tracking-tight">{title}</h3>

        {description ? (
          <p className="mt-2 max-w-sm text-sm text-muted-foreground">
            {description}
          </p>
        ) : null}

        {buttonText ? (
          <Button
            className="mt-4"
            variant={buttonVariant}
            onClick={onButtonClick}
            disabled={buttonDisabled}
          >
            {buttonIcon}
            {buttonText}
          </Button>
        ) : null}
      </div>
    </div>
  );
}

export default EmptyState;
